import { EventEmitter, Injectable, Output, Directive } from '@angular/core';
import { isString } from 'util';
import { PersonalizationsService } from '../../base/services/personalizations/personalizations.service';
import { Environment } from '../../models/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { DisplayState } from '../enums/signaturit/signaturit-enums';
import { NotificationService } from '../../base/services/notification/notification-service';
import { LoadingService } from '../loading/loading.service';

export declare interface MessageInterface {
  type: string;
  title?: string;
  message: string;
  code?: string;
  traceid?: string;
}

@Directive()
@Injectable()
export class MessageService {

  environment: Environment;
  customEvents: boolean;
  @Output() onNew: EventEmitter<MessageInterface> = new EventEmitter<MessageInterface>();

  constructor(
    private _personalization: PersonalizationsService,
    private _notification: NotificationService,
    private _loading: LoadingService) {
    this.environment = this._personalization.sendEnv;
    this.customEvents = this.environment.features.customEvents || false;
  }

  public show(message: string, type: string = 'info', title: string = '', code: string = '', traceid: string = ''): void {
    this.onNew.emit({ message, type, title, code, traceid });
  }

  public showError(message: any, title?: string): void {
    const traceid = this.renderTraceid(message);
    const code = this.getCodeError(message);
    const errorMessage = this.renderError(message, () => this.show(errorMessage, 'error', title || 'Error', code, traceid));

    if (this.customEvents) {
      window.top.postMessage({ type: 'onError', result: 'Error', message: errorMessage, traceid: traceid }, window.parent.origin);
    }

    if (!errorMessage) {
      return;
    }

    this.show(errorMessage, 'error', title || 'Error', code, traceid);
  }

  public renderError(message: any, callback?: Function): string {
    if (typeof message === 'string') {
      return message;
    }

    while (message.error && !isString(message.error)) {
      message = message.error;
    }

    if (message.Description) {
      return message.Description;
    }

    if (message.message) {
      return message.message;
    }

    if (message instanceof Blob && callback) {
      const reader = new FileReader();
      reader.readAsText(message);
      reader.onloadend = () => {
        this.showError(JSON.parse(reader.result.toString()));
      };
    }

    if (message._body) {
      return JSON.parse(message._body)?.Description;
    }

    if (message.statusText) {
      return message.statusText;
    }

    if (message.error_description) {
      return message.error_description;
    }

    if (message.error) {
      return message.error;
    }

    return message;
  }

  private renderTraceid(message: any): string {
    const traceid = this.getTraceId(message);

    if (traceid) {
      return traceid;
    }

    if (message.traceid) {
      return message.traceid;
    }

    if (message._body) {
      return JSON.parse(message._body)?.traceid;
    }

    return null;
  }

  private getTraceId(message: any): string {
    if (message.error) {
      if (message.error?.error?.traceid) {
        return message.error.error.traceid;
      }

      if (message.error?.traceid) {
        return message.error.traceid;
      }

      if (message.error?._body) {
        return JSON.parse(message.error._body)?.traceid;
      }
    }
  }

  private getCodeError(message: any): string {
    return message?.error?.error?.code;
  }

  private setPositionMessage(): void {
    const containerNotif = document.getElementById('sig-notification');

    if (window.scrollY < 72) {
      containerNotif.style.top = '78px';
    } else {
      containerNotif.style.top = '0';
    }
  }

  public showSuccessMessage(message: string): string {
    this.setPositionMessage();
    this._notification.activeNotif(DisplayState.SUCCESS);

    return message;
  }

  public showCustomErrorMessage(message: string): string {
    this.setPositionMessage();
    this._notification.activeNotif(DisplayState.ERROR);

    return message;
  }

  public showHttpErrorMessage(message: HttpErrorResponse): string {
    this.setPositionMessage();
    this._loading?.set(false);
    this._notification.activeNotif(DisplayState.ERROR);

    const code = message?.error?.error?.code;
    const descriptiveMessage = message?.error?.error?.message;

    if (descriptiveMessage) {
      return code ? `${code}: ${descriptiveMessage}` : descriptiveMessage;
    }

    return message?.message;
  }

}
